import type { AxiosResponse, AxiosError } from 'axios';
import { ElMessage, ElMessageBox } from 'element-plus';
import { $t } from '@/i18n';
import { storeToRefs } from 'pinia';
import useUserStore from '@/store/user.store';
import { ApiCodeEnum, BetBusApiCodeEnum } from '@/const/api.const';
import type { ApiResponse } from '@/plugins/api';
import { openDialog } from '@/utils/dialog'; //打开弹框工具方法
import PasswordExpiredDialog from '@/components/layout/Nav/ChangePwdDialog.vue'; //密码过期弹框

/**
 * 错误码配置映射表
 * 使用 Map 提升查找性能
 */
const ERROR_CODE_MAP = new Map<number, {
  title: string,
  message: string,
  handler?: () => void,
}>([
      // 通用错误
      [ApiCodeEnum.UnknownError, {
        title: 'api.code.-1',
        message: 'api.message.-1'
      }],
      [ApiCodeEnum.Failure, {
        title: 'api.code.7',
        message: 'api.message.7'
      }],

      // 认证相关错误 - 需要登出
      [BetBusApiCodeEnum.UnauthAccess, {
        title: 'api.code.3019',
        message: 'api.message.3019',
        handler: handleLogout
      }],
      [BetBusApiCodeEnum.InvaildToken, {
        title: 'api.code.3020',
        message: 'api.message.3020',
        handler: handleLogout
      }],
      [BetBusApiCodeEnum.ForceQuit, {
        title: 'api.code.50001',
        message: 'api.message.50001',
        handler: handleLogout
      }],
      [BetBusApiCodeEnum.CurrentAccountNotMatch, {
        title: 'api.code.5001',
        message: 'api.message.5001',
        handler: handleLogout
      }],
      [BetBusApiCodeEnum.TheTokenHasExpired, {
        title: 'api.code.5015',
        message: 'api.message.5015',
        handler: handleLogout
      }],

      // 用户相关错误
      [BetBusApiCodeEnum.ExistedUsername, {
        title: 'api.code.1017',
        message: 'api.message.1017'
      }],
      [BetBusApiCodeEnum.LoginPasswordError, {
        title: 'api.code.1003',
        message: 'api.message.1003'
      }],
      [BetBusApiCodeEnum.LoginFailBlack, {
        title: 'api.code.1031',
        message: 'api.message.1031'
      }],
      [BetBusApiCodeEnum.PasswordFormatError, {
        title: 'api.code.1129',
        message: 'api.message.1129'
      }],
      [BetBusApiCodeEnum.AccountHasBeenFrozen, {
        title: 'api.code.1001',
        message: 'api.message.1001'
      }],
      [BetBusApiCodeEnum.IncorrectUsernameOrPassword, {
        title: 'api.code.1049',
        message: 'api.message.1049'
      }],
      [BetBusApiCodeEnum.DuplicatedPassword, {
        title: 'api.code.5003',
        message: 'api.message.5003'
      }],
      [BetBusApiCodeEnum.NotLoggedInOrIllegalAccess, {
        title: 'api.code.5012',
        message: 'api.message.5012'
      }],

      // 权限相关错误
      [BetBusApiCodeEnum.AuthorityNNameExisted, {
        title: 'api.code.5000',
        message: 'api.message.5000'
      }]
    ]);

/**
 * 处理登出逻辑
 */
function handleLogout() {
  const userStore = useUserStore();

  userStore.logout();

  // 延迟跳转，确保状态清理完成
  setTimeout(() => {
    window.location.href = '/login';
  }, 100);
}

/**
 * 处理密码过期
 */
function handlePasswordExpired(message: string) {
  const userStore = useUserStore();
  const { passwordExpired } = storeToRefs(userStore);

  if (passwordExpired.value) {
    return;
  }

  ElMessageBox.confirm(
    message || $t('api.PasswordExpired'),
    $t('api.Warning'),
    {
      confirmButtonText: $t('api.ResetPassword'),
      cancelButtonText: $t('api.Cancel'),
      type: 'warning',
      closeOnClickModal: false,
      closeOnPressEscape: false,
      showClose: false
    }
  ).then(() => {
    openDialog(PasswordExpiredDialog, {
      props: {
        scene: 2
      },
      modelProp: 'isShowPwdDialog'

    });
  }).catch(() => {
    // 用户取消，强制登出
    handleLogout();
  });
}

/**
 * 显示错误通知
 */
function showErrorNotification(message: string) {
  ElMessage({
    message,
    type: 'warning',
    duration: 3000,
    showClose: true
  });
}

/**
 * 处理 API 错误响应
 *
 * @param response - Axios 响应对象
 * @param config - 请求配置（可选）
 * @returns Promise.reject
 *
 * @example
 * ```typescript
 * axios.interceptors.response.use(
 *   (response) => {
 *     if (response.data.code !== 200) {
 *       return handleErrorResponse(response);
 *     }
 *     return response;
 *   }
 * );
 * ```
 */
export function handleErrorResponse(
  response: AxiosResponse<ApiResponse>,
  options: {
    noNotify?: boolean,
  } = {}
): Promise<never> {
  const { data, config } = response;
  const { noNotify = false } = options;

  // 如果配置了不显示通知，直接返回
  if (noNotify || (config as any)?.noNotify) {
    return Promise.reject(response);
  }

  // 特殊处理：密码过期
  if (data.code === BetBusApiCodeEnum.PasswordExpiredCode) {
    handlePasswordExpired(data.message);

    return Promise.reject(response);
  }

  // 从错误码映射表中查找配置
  const errorConfig = ERROR_CODE_MAP.get(data.code);

  if (errorConfig) {
    // 显示错误消息
    const message = $t(errorConfig.message);

    showErrorNotification(message);

    // 执行特定处理函数（如登出）
    if (errorConfig.handler) {
      errorConfig.handler();
    }
  } else {
    // 未配置的错误码，显示原始消息
    showErrorNotification(data.message || $t('api.UnknownError'));
  }

  return Promise.reject(response);
}

export function addErrorCodeConfig(
  code: number,
  config: {
    title: string,
    message: string,
    handler?: () => void,
  }
) {
  ERROR_CODE_MAP.set(code, config);
}

/**
 * 批量添加错误码配置
 *
 * @param configs - 错误码配置数组
 */
export function addErrorCodeConfigs(
  configs: Array<{
    code: number,
    title: string,
    message: string,
    handler?: () => void,
  }>
) {
  configs.forEach(({ code, ...config }) => {
    ERROR_CODE_MAP.set(code, config);
  });
}

/**
 * 检查是否需要登出的错误码
 */
export function isLogoutError(code: number): boolean {
  return [
    BetBusApiCodeEnum.UnauthAccess,
    BetBusApiCodeEnum.InvaildToken,
    BetBusApiCodeEnum.ForceQuit,
    BetBusApiCodeEnum.CurrentAccountNotMatch,
    BetBusApiCodeEnum.TheTokenHasExpired
  ].includes(code);
}

/**
 * 检查是否为密码过期错误
 */
export function isPasswordExpiredError(code: number): boolean {
  return code === BetBusApiCodeEnum.PasswordExpiredCode;
}

/**
 * HTTP 状态码错误配置映射表
 */
const HTTP_ERROR_MAP = new Map<number, {
  message: string,
  handler?: () => void,
}>([
      [400, {
        message: 'api.http.400'
      }],
      [401, {
        message: 'api.http.401',
        handler: () => {
          const userStore = useUserStore();

          userStore.logout();
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
      }],
      [403, {
        message: 'api.http.403'
      }],
      [404, {
        message: 'api.http.404'
      }],
      [500, {
        message: 'api.http.500'
      }],
      [502, {
        message: 'api.http.502'
      }],
      [503, {
        message: 'api.http.503'
      }],
      [504, {
        message: 'api.http.504'
      }]
    ]);

/**
 * 处理网络错误
 *
 * @param error - Axios 错误对象
 * @returns Promise.reject
 *
 * @example
 * ```typescript
 * axios.interceptors.response.use(
 *   (response) => response,
 *   (error) => handleNetworkError(error)
 * );
 * ```
 */
export function handleNetworkError(error: AxiosError): Promise<never> {
  console.error('[Network Error]:', error);

  let message = $t('api.http.NetworkError');

  // 有响应 - HTTP 错误状态码
  if (error.response) {
    const status = error.response.status;
    const errorConfig = HTTP_ERROR_MAP.get(status);

    if (errorConfig) {
      message = $t(errorConfig.message);

      // 执行特定处理函数（如登出）
      if (errorConfig.handler) {
        errorConfig.handler();
      }
    } else {
      // 未配置的状态码，尝试使用响应中的消息
      message = (error.response.data as any)?.message || $t('api.http.RequestFailed');
    }
  } else if (error.request) {
    // 有请求但无响应 - 网络问题
    if (error.code === 'ECONNABORTED') {
      message = $t('api.http.Timeout');
    } else if (error.message === 'Network Error') {
      message = $t('api.http.NetworkError');
    } else {
      message = $t('api.http.NoResponse');
    }
  } else {
    // 请求配置错误
    message = error.message || $t('api.http.RequestFailed');
  }

  ElMessage.error(message);

  return Promise.reject(error);
}

/**
 * 添加 HTTP 错误码配置
 *
 * @param status - HTTP 状态码
 * @param config - 错误配置
 */
export function addHttpErrorConfig(
  status: number,
  config: {
    message: string,
    handler?: () => void,
  }
) {
  HTTP_ERROR_MAP.set(status, config);
}
